import { useEffect, useRef } from 'react';

import { useRouter } from 'react-resource-router';

import { getDocument } from '@atlaskit/browser-apis';

import { scrollToTop } from './scrollToTop';

import { resolveAnchorId } from './index';

/**
 * Returns the viewport to the top when the route changes. Without this, an SPA
 * navigation between `/` and `/cli` keeps the previous scroll offset, so a
 * visitor partway down the home page arrives on `/cli` looking at its footer.
 *
 * Navigations whose URL carries a resolvable section hash are left alone. Those
 * are deep links, and `useScrollToHash` on the destination page polls for the
 * lazily rendered section (for up to five seconds) before scrolling to it.
 * Both hooks read the same `location.hash` through `resolveAnchorId`, so at most
 * one of them acts on any given navigation: the reset can neither cut a pending
 * poll short nor yank the page back up after the poll lands.
 *
 * The first render is intentionally a no-op, since a full page load is the
 * browser's own scroll restoration to manage.
 */
export const useResetScrollOnRouteChange = (): void => {
	const [{ route }] = useRouter();
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (resolveAnchorId(getDocument()?.location.hash ?? '')) {
			return;
		}
		scrollToTop();
	}, [route?.name]);
};
